import "./styles.scss";
import React, { useCallback, useState, useEffect } from "react";
import { Badge, Button, Checkbox, Input, Label, Switch, Spinner } from "@fluentui/react-components";
import { ArrowClockwiseRegular, Edit24Filled, FoodRegular, QrCode24Filled } from "@fluentui/react-icons";
import Paginator from "#root/components/Paginator";
import LoadingWrapper from "#root/components/LoadingWrapper";
import { Breadcrumbs } from "#root/components/Breadcrumbs";
import { getEnumLabelByValue } from "#root/utils/enums";
import LazyImage from "#root/components/LazyImage";
import DateNavigator from "#root/components/DateNavigator";
import {
  useGetMealTrackingList,
  useGetMealTrackingStatistics,
  useUpdateMealTracking,
  useGetMealTrackingById
} from "#root/queries/mealTracking";
import { RESPONDER_ROLES_CONST } from "#root/utils/responders";
import { DialogBox } from "#root/components/DialogBox";
import { showToast } from "#root/components/GlobalToaster";
import { MealTracking as MealTrackingType } from "#root/queries/mealTracking";
import ResponderFilters, { FilterState, initialFilters } from "#root/components/ResponderFilters";
import { useIncidentById } from "#root/queries/incidents";
import QrScanner from "#root/components/QrScanner";
import ButtonGroup from "#root/components/ButtonGroup";
import {
  EMPTY_ID,
  MEAL_OPTIONS,
  ID_LENGTH,
  INITIAL_ITEMS_PER_PAGE,
  INITIAL_SNACKS,
  INITIAL_QUANTITY
} from "#root/utils/mealTracking";

interface MealTrackingFormData extends MealTrackingType { }

const MealTracking: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(INITIAL_ITEMS_PER_PAGE);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isQrDialogOpen, setIsQrDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState<MealTrackingFormData | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filters, setFilters] = useState<FilterState>(() => ({ ...initialFilters }));
  const [minDate, setMinDate] = useState<Date | undefined>(undefined);
  const [maxDate, setMaxDate] = useState<Date | undefined>(new Date());
  const [isQrScannerOpen, setIsQrScannerOpen] = useState(false);
  const [qrParams, setQrParams] = useState<{ id: string, date: Date }>({ id: EMPTY_ID, date: new Date() });
  const [mealValue, setMealValue] = useState<string>(MEAL_OPTIONS.snacks.value);
  const [groupPickup, setGroupPickup] = useState<boolean>(false);
  const [quantity, setQuantity] = useState<number>(INITIAL_QUANTITY);
  const { data: incidentData } = useIncidentById({ id: filters.incidentId || '' });
  const [qrFetchParams, setQrFetchParams] = useState<{ id: string, date: Date } | null>(null);
  const selectedIncident = incidentData?.item;

  const {
    data: statisticsResponse,
    refetch: refetchStatistics
  } = useGetMealTrackingStatistics({
    date: selectedDate,
    ...filters
  });

  const {
    data: mealTrackingResponse,
    isLoading: mealTrackingLoading,
    refetch: refetchMealTracking
  } = useGetMealTrackingList({
    skip: (currentPage - 1) * itemsPerPage,
    take: itemsPerPage,
    date: selectedDate,
    ...filters
  });

  const {
    data: qrMealTrackingByIdData,
    isFetching: isQrMealTrackingByIdLoading,
  } = useGetMealTrackingById(
    qrFetchParams
      ? { id: qrFetchParams.id, date: qrFetchParams.date }
      : { id: EMPTY_ID, date: new Date() },
    { enabled: !!qrFetchParams && qrFetchParams.id !== EMPTY_ID }
  );

  const updateMealTrackingMutation = useUpdateMealTracking();

  const totalCount = mealTrackingResponse?.totalCount ?? 0;
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const mealTracking = mealTrackingResponse?.items ?? [];
  const statistics = statisticsResponse?.item ?? {};

  useEffect(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    if (selectedIncident) {
      const startDate = selectedIncident.startDate ? new Date(selectedIncident.startDate) : undefined;
      const endDate = selectedIncident.endDate ? new Date(selectedIncident.endDate) : today;
      const effectiveEndDate = endDate > today ? today : endDate;

      setMinDate(startDate);
      setMaxDate(effectiveEndDate);

      if (startDate && selectedDate < startDate) {
        setSelectedDate(startDate);
      } else if (selectedDate > effectiveEndDate) {
        setSelectedDate(effectiveEndDate);
      }
    } else {
      setMinDate(undefined);
      setMaxDate(today);
    }
  }, [selectedIncident, selectedDate]);

  const handleItemsPerPageChange = (newItemsPerPage: number): void => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    refetchStatistics();
    refetchMealTracking();
  };

  const handleDateChange = (date: Date) => {
    setSelectedDate(date);
    refetchStatistics();
    refetchMealTracking();
  };

  const handleQRCode = () => {
    setIsQrScannerOpen(true);
  };

  const handleQrDialogClose = () => setIsQrScannerOpen(false);

  const handleFilterChange = useCallback((field: keyof FilterState, value: any) => {
    setTimeout(() => {
      setFilters(prev => ({
        ...prev,
        [field]: value === '' ? undefined : value
      }));
      setCurrentPage(1);

      if (field === 'incidentId') {
        refetchStatistics();
        refetchMealTracking();
      }
    }, 50);
  }, [refetchStatistics, refetchMealTracking]);

  const handleEditMealAccess = (responder: MealTrackingFormData) => {
    setDialogData(responder);
    setIsDialogOpen(true);
  };

  const handleQrScanById = (responderId: string) => {
    setIsQrScannerOpen(false);
    if (!responderId || responderId === EMPTY_ID) {
      setIsQrDialogOpen(false);
      showToast('Incorrect QR code please try again', 'error');
      setQrParams({ id: EMPTY_ID, date: new Date() });
      setQrFetchParams(null);
      return;
    }
    setIsDialogOpen(false);
    setIsQrDialogOpen(true);
    setQrParams({ id: responderId, date: selectedDate });
    setQrFetchParams({ id: responderId, date: selectedDate });
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setIsQrDialogOpen(false);
    setDialogData(null);
    setQrParams({ id: EMPTY_ID, date: new Date() });
    setMealValue(MEAL_OPTIONS.snacks.value);
    setGroupPickup(false);
    setQuantity(INITIAL_QUANTITY);
  };

  const handleFormChange = (field: keyof MealTrackingFormData, value: any) => {
    if (dialogData) {
      setDialogData({
        ...dialogData,
        [field]: value
      });
    }
  };

  function getQrDialogData() {
    if (qrParams && mealTracking && mealTracking.length) {
      const found = mealTracking.find(r => r.id === qrParams.id);
      if (found) return found;
    }
    if (isQrMealTrackingByIdLoading) return null;
    if (qrMealTrackingByIdData && qrMealTrackingByIdData.item) {
      return qrMealTrackingByIdData.item;
    }
    return null;
  }
  const qrDialogData = getQrDialogData();

  const handleSave = async (isQrDialog = false) => {
    const dataToSave = isQrDialog ? qrDialogData : dialogData;

    if (dataToSave) {
      if (!dataToSave.id || typeof dataToSave.id !== "string" || dataToSave.id.length !== ID_LENGTH) {
        showToast('Invalid QR code. Please try again.', 'error');
        return;
      }

      let updateData: any = {
        ...dataToSave,
        date: selectedDate,
        responderId: dataToSave.id,
      };

      if (isQrDialog) {
        if (mealValue === MEAL_OPTIONS.breakfast.value) {
          updateData.breakfast = groupPickup ? dataToSave.breakfast : true;
          updateData.breakfastQuantity = groupPickup ? quantity : dataToSave.breakfastQuantity;
        }
        if (mealValue === MEAL_OPTIONS.lunch.value) {
          updateData.lunch = groupPickup ? dataToSave.lunch : true;
          updateData.lunchQuantity = groupPickup ? quantity : dataToSave.lunchQuantity;
        }
        if (mealValue === MEAL_OPTIONS.dinner.value) {
          updateData.dinner = groupPickup ? dataToSave.dinner : true;
          updateData.dinnerQuantity = groupPickup ? quantity : dataToSave.dinnerQuantity;
        }
        if (mealValue === MEAL_OPTIONS.snacks.value) {
          if (!groupPickup) {
            const prevSnacksCount = Number(dataToSave.snacks) || 0;
            updateData.snacks = prevSnacksCount + 1;
            updateData.snacksQuantity = dataToSave.snacksQuantity;
          } else {
            const prevSnacksQuantity = Number(dataToSave.snacksQuantity) || 0;
            updateData.snacks = dataToSave.snacks;
            updateData.snacksQuantity = prevSnacksQuantity + quantity;
          }
        }
      } else {
        updateData = {
          ...dataToSave,
          date: selectedDate,
          responderId: dataToSave.id,
        };
      }

      try {
        setIsSubmitting(true);
        await updateMealTrackingMutation.mutateAsync({ mealTracking: updateData });
        showToast('Meal tracking updated successfully', 'success');
        handleRefresh();
        handleDialogClose();
      } catch (error) {
        showToast('Failed to update meal tracking', 'error');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const mealOptionsForQr = Object.values(MEAL_OPTIONS).map(opt => {
    if (opt.value === MEAL_OPTIONS.breakfast.value) return { ...opt, disabled: qrDialogData?.breakfast };
    if (opt.value === MEAL_OPTIONS.lunch.value) return { ...opt, disabled: qrDialogData?.lunch };
    if (opt.value === MEAL_OPTIONS.dinner.value) return { ...opt, disabled: qrDialogData?.dinner };
    return opt;
  });

  const renderResponderHeader = ({ fullName, personalImageUrl, role }: { fullName: string, personalImageUrl: string, role: any }) => {
  return (
    <div className="responder-info">
      <div className="responder-info__image">
        <div className="person-image">
          <LazyImage
            src={personalImageUrl}
            alt={fullName || 'Responder'}
            fallback={<div className="person-image--no-image" />}
          />
        </div>
      </div>
      <div className="responder-info__info">
        <p className="responder-info__name">{fullName}</p>
        <p className="responder-info__role">
          {getEnumLabelByValue(RESPONDER_ROLES_CONST, role)}
        </p>
      </div>
    </div>
  );
};

  return (
    <div className="meal-tracking">
      <div className="emc-page-header">
        <div className="emc-page-header__top-row">
          <div className="emc-page-header__breadcrumbs">
            <Breadcrumbs />
          </div>
          <div className="emc-page-header__props">
            <Badge size="extra-large" color="success">
              Full Access: {statistics.fullAccess ?? '-'}
            </Badge>
            <Badge size="extra-large" color="success" appearance="tint">
              Breakfast: {statistics.breakfast ?? '-'}
            </Badge>
            <Badge size="extra-large" color="success" appearance="tint">
              Lunch: {statistics.lunch ?? '-'}
            </Badge>
            <Badge size="extra-large" color="success" appearance="tint">
              Dinner: {statistics.dinner ?? '-'}
            </Badge>
            <Badge size="extra-large" color="success" appearance="tint">
              Snacks: {statistics.snacks ?? '-'}
            </Badge>
            <Button
              appearance="primary"
              shape="circular"
              icon={<ArrowClockwiseRegular />}
              onClick={handleRefresh}
            />
          </div>
        </div>

        <div className="emc-page-header__top-row">
        </div>

        <div className="emc-page-header__title">
          <h2>
            Meal Tracking <div className="divider" />
            <DateNavigator
              initialDate={selectedDate}
              onDateChange={handleDateChange}
              minDate={minDate}
              maxDate={maxDate}
            />
            <Button
              appearance="primary"
              icon={<QrCode24Filled />}
              onClick={handleQRCode}
            >
              Scan QR Code
            </Button>
          </h2>
        </div>
      </div>
      
      <ResponderFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        className="responders-list__filters"
        departmentFilterEnabled={false}
      />
      
      <LoadingWrapper
        isLoading={mealTrackingLoading}
        isEmpty={mealTracking.length === 0}
        emptyMessage={`No responders found.`}
        emptyIcon={<FoodRegular />}
      >
        <div>
          <table className="emc-table">
            <thead>
              <tr>
                <th>Picture</th>
                <th>Name</th>
                <th>Role</th>
                <th>Breakfast</th>
                <th>Lunch</th>
                <th>Dinner</th>
                <th>Snacks</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {mealTracking.map((record) => (
                <tr key={record.id}>
                  <td>
                    <div className="person-image">
                      <LazyImage
                        src={record.personalImageUrl}
                        alt={record.fullName || 'Responder'}
                        fallback={<div className="person-image--no-image" />}
                      />
                    </div>
                  </td>
                  <td>{record.fullName}</td>
                  <td>{getEnumLabelByValue(RESPONDER_ROLES_CONST, record.role)}</td>
                  <td>
                    <Badge className="status-badge" shape="circular" size="large" color={record.breakfast ? "success" : "danger"}>
                      {record.breakfast ? "✓" : "✕"}
                    </Badge>
                  </td>
                  <td>
                    <Badge className="status-badge" shape="circular" size="large" color={record.lunch ? "success" : "danger"}>
                      {record.lunch ? "✓" : "✕"}
                    </Badge>
                  </td>
                  <td>
                    <Badge className="status-badge" shape="circular" size="large" color={record.dinner ? "success" : "danger"}>
                      {record.dinner ? "✓" : "✕"}
                    </Badge>
                  </td>
                  <td>
                    <Badge className="status-badge" shape="circular" size="large" color={record.snacks > 0 ? "success" : "danger"}>
                      {record.snacks}
                    </Badge>
                  </td>
                  <td>
                    <Button appearance="transparent" icon={<Edit24Filled />} onClick={() => handleEditMealAccess(record)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Paginator
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            totalItems={totalCount}
            currentItems={(currentPage - 1) * itemsPerPage + 1}
            showItemsPerPage={true}
          />
        </div>
      </LoadingWrapper>
      
      {isQrScannerOpen && (
        <QrScanner
          onScan={() => setIsQrScannerOpen(false)}
          onClose={handleQrDialogClose}
          getResponderId={handleQrScanById}
        />
      )}
      
      <DialogBox
        open={isDialogOpen}
        onOpenChange={(open) => { if (!open) handleDialogClose(); }}
        title="Meal Access"
        primaryButtonText="Save"
        onPrimaryButtonClick={() => handleSave(false)}
        isPrimaryButtonLoading={isSubmitting}
        secondaryButtonText="Cancel"
        onSecondaryButtonClick={handleDialogClose}
      >
        {dialogData ? (
          <div className="meal-tracking-dialog">
            {renderResponderHeader({
              fullName: dialogData.fullName,
              personalImageUrl: dialogData.personalImageUrl || '',
              role: dialogData.role
            })}
            <form className="meal-tracking__form meal-tracking__form--row">
              <Checkbox
                label={MEAL_OPTIONS.breakfast.label || ''}
                checked={dialogData.breakfast}
                onChange={(_, data) => handleFormChange(MEAL_OPTIONS.breakfast.value as keyof MealTrackingFormData, data.checked)}
              />
              <Checkbox
                label={MEAL_OPTIONS.lunch.label || ''}
                checked={dialogData.lunch}
                onChange={(_, data) => handleFormChange(MEAL_OPTIONS.lunch.value as keyof MealTrackingFormData, data.checked)}
              />
              <Checkbox
                label={MEAL_OPTIONS.dinner.label || ''}
                checked={dialogData.dinner}
                onChange={(_, data) => handleFormChange(MEAL_OPTIONS.dinner.value as keyof MealTrackingFormData, data.checked)}
              />
              <div className="meal-tracking__snacks">
                <Input
                  id="snacks-input"
                  type="number"
                  value={dialogData.snacks.toString()}
                  onChange={(_, data) => handleFormChange(MEAL_OPTIONS.snacks.value as keyof MealTrackingFormData, parseInt(data.value) || INITIAL_SNACKS)}
                  min={INITIAL_SNACKS}
                />
                <Label htmlFor="snacks-input">{MEAL_OPTIONS.snacks.label || ''}</Label>
              </div>
            </form>
          </div>
        ) : (
          <div className="dialog-loading">
            Loading...
          </div>
        )}
      </DialogBox>
      
      <DialogBox
        open={isQrDialogOpen}
        onOpenChange={(open) => { if (!open) handleDialogClose(); }}
        title="Meal Access"
        primaryButtonText="Save"
        onPrimaryButtonClick={() => handleSave(true)}
        isPrimaryButtonLoading={isSubmitting || isQrMealTrackingByIdLoading}
        secondaryButtonText="Cancel"
        onSecondaryButtonClick={handleDialogClose}
      >
        {isQrMealTrackingByIdLoading ? (
          <Spinner />
        ) : qrDialogData ? (
          <div className="meal-tracking-dialog custom-meal-dialog">
            {renderResponderHeader({
              fullName: qrDialogData.fullName,
              personalImageUrl: qrDialogData.personalImageUrl || '',
              role: qrDialogData.role
            })}

            <form className="meal-tracking__form">
              <div className="meal-type-group">
                <ButtonGroup
                  options={mealOptionsForQr}
                  value={mealValue}
                  onChange={(val) => setMealValue(val)}
                  multiple={false}
                  name="mealType"
                />
              </div>
              
              <div className="group-pickup-row">
                <Switch
                  checked={groupPickup}
                  onChange={(_, data) => setGroupPickup(data.checked)}
                  className="group-pickup-switch"
                  label="Group Pickup"
                />
                {groupPickup && (
                  <div className="quantity-group">
                    <span className="quantity-label">Quantity</span>
                    <Input
                      id="group-quantity"
                      type="number"
                      min={INITIAL_QUANTITY}
                      value={quantity.toString()}
                      onChange={(_, data) =>
                        setQuantity(Math.max(Number(data.value) || INITIAL_QUANTITY, INITIAL_QUANTITY))
                      }
                      onKeyDown={(e) => {
                        if (e.key === '-' || e.key === 'Minus') e.preventDefault();
                      }}
                      size="small"
                      className="quantity-input"
                    />
                  </div>
                )}
              </div>

            </form>
          </div>
        ) : (
          <div className="dialog-loading">Not found</div>
        )}
      </DialogBox>
    </div>
  );
};

export default MealTracking;  